/**
 * The streak as the running game uses it: the gate, the hand-off of "something happened today" from
 * the round to the screen that celebrates it, and the dev seed (specs/day-streak.md).
 *
 * The pure arithmetic lives in streak.ts; this file is the only place that touches the clock, the
 * platform or analytics.
 */
import { isClipMode } from '@/game/clip';
import { devParam } from '@/game/dev';
import { trackStreakDay } from '@/analytics';
import {
  addDays, emptyStreak, loadStreak, localYmd, recordSmash, saveStreak, streakBucket, viewOf,
  type StreakData, type StreakEvent, type StreakView,
} from './streak';

/**
 * Off in the App Clip trial and the `?demo` session: those are capped sales flows, and a streak
 * would promise something the capped build cannot keep (spec §3).
 */
export function streakEnabled(): boolean {
  return !isClipMode();
}

let pending: StreakEvent | null = null;

/** One correct smash. Cheap: the common case (day already counted) does one storage read. */
export function registerSmash(now: Date = new Date()): void {
  if (!streakEnabled()) return;
  const today = localYmd(now);
  const before = loadStreak();
  const { data, event } = recordSmash(before, today);
  if (data !== before) saveStreak(data);
  if (event) {
    // A second event the same day cannot happen (the day then counts as played); if a milestone
    // event is still unclaimed we keep the more important of the two.
    pending = event;
    trackStreakDay(event.kind, streakBucket(event.current));
  }
}

/** The celebration owed to the player, once. Whichever screen shows first claims it. */
export function takeStreakEvent(): StreakEvent | null {
  const e = pending;
  pending = null;
  return e;
}

export function currentStreakView(now: Date = new Date()): StreakView {
  return viewOf(loadStreak(), localYmd(now));
}

let seeded = false;

/**
 * Dev/staging only: `?streak=6` puts the device on a 6-day streak last played yesterday, so the next
 * five smashes ignite day 7 (the milestone and the freeze). `?streak=cold` is a streak broken a week
 * ago. Ignored on production by devParam().
 */
export function maybeSeedStreak(now: Date = new Date()): void {
  if (seeded) return;
  const raw = devParam('streak');
  if (raw === null) return;
  seeded = true;
  const today = localYmd(now);
  let data: StreakData = emptyStreak();
  if (raw === 'cold') {
    data = { ...data, current: 12, best: 12, lastDay: addDays(today, -7), freezes: 0, milestonesSeen: [3, 7] };
  } else {
    const n = Math.max(1, Math.min(400, parseInt(raw, 10) || 1));
    data = {
      ...data, current: n, best: n, lastDay: addDays(today, -1), freezes: n >= 7 ? 1 : 0,
      milestonesSeen: [3, 7, 14, 30, 60, 100, 365].filter((m) => m <= n),
      week: Array.from({ length: Math.min(6, n) }, (_, i) => addDays(today, -1 - i)).reverse(),
    };
  }
  saveStreak(data);
}
